/**
 * Shared vocabulary and pure helpers for sequence relationships:
 * prerequisite (preceding) vs dependent (succeeding), item edges and planning timing.
 */

import type { DependencyTypeEnum } from '../types/planning';
import type { DependencySubtype, ItemRelationshipEdge } from '../types/prioritization/itemRelationships';

export const PREREQUISITE_DEFINITION =
  'A preceding item or condition that must be satisfied before another can proceed.';

export const DEPENDENT_DEFINITION =
  'A succeeding item that waits on a prerequisite — it cannot proceed until the prerequisite is resolved.';

export const DEPENDENCY_TIMING_LABELS: Record<
  DependencyTypeEnum,
  { short: string; description: string }
> = {
  FS: {
    short: 'Finish → Start',
    description: 'The dependent cannot start until the prerequisite is completed.',
  },
  SS: {
    short: 'Start → Start',
    description: 'The dependent cannot start until the prerequisite has started.',
  },
  FF: {
    short: 'Finish → Finish',
    description: 'The dependent cannot finish until the prerequisite is completed.',
  },
  SF: {
    short: 'Start → Finish',
    description: 'The dependent cannot finish until the prerequisite has started.',
  },
};

export interface NormalizedSequencePair {
  predecessorItemId: string;
  dependentItemId: string;
  edgeId: string;
  edgeType: 'dependency' | 'prerequisite';
}

export interface SequenceNeighbor {
  itemId: string;
  edge: ItemRelationshipEdge;
  via: 'dependency' | 'prerequisite';
}

export interface SequenceRoleItemIds {
  /** Items with one or more predecessors that must be addressed before them. */
  hasPredecessors: Set<string>;
  /** Items with one or more dependents waiting on them. */
  hasDependents: Set<string>;
}

function isActiveSequenceEdge(edge: ItemRelationshipEdge): boolean {
  return (
    (edge.type === 'dependency' || edge.type === 'prerequisite') &&
    edge.status !== 'dissolved'
  );
}

/**
 * Normalize a dependency or prerequisite edge to predecessor → dependent.
 * - dependency: source = dependent, target = prerequisite
 * - prerequisite: source = prerequisite, target = dependent
 */
export function normalizeSequencePair(edge: ItemRelationshipEdge): NormalizedSequencePair | null {
  if (!isActiveSequenceEdge(edge)) return null;

  if (edge.type === 'dependency') {
    return {
      predecessorItemId: edge.targetItemId,
      dependentItemId: edge.sourceItemId,
      edgeId: edge.id,
      edgeType: 'dependency',
    };
  }

  return {
    predecessorItemId: edge.sourceItemId,
    dependentItemId: edge.targetItemId,
    edgeId: edge.id,
    edgeType: 'prerequisite',
  };
}

/** Items that must come before `itemId`. */
export function getPredecessorsForItem(itemId: string, edges: ItemRelationshipEdge[]): SequenceNeighbor[] {
  const results: SequenceNeighbor[] = [];
  for (const edge of edges) {
    if (!isActiveSequenceEdge(edge)) continue;
    if (edge.type === 'dependency' && edge.sourceItemId === itemId) {
      results.push({ itemId: edge.targetItemId, edge, via: 'dependency' });
    } else if (edge.type === 'prerequisite' && edge.targetItemId === itemId) {
      results.push({ itemId: edge.sourceItemId, edge, via: 'prerequisite' });
    }
  }
  return results;
}

/** Items that wait on / come after `itemId`. */
export function getDependentsForItem(itemId: string, edges: ItemRelationshipEdge[]): SequenceNeighbor[] {
  const results: SequenceNeighbor[] = [];
  for (const edge of edges) {
    if (!isActiveSequenceEdge(edge)) continue;
    if (edge.type === 'dependency' && edge.targetItemId === itemId) {
      results.push({ itemId: edge.sourceItemId, edge, via: 'dependency' });
    } else if (edge.type === 'prerequisite' && edge.sourceItemId === itemId) {
      results.push({ itemId: edge.targetItemId, edge, via: 'prerequisite' });
    }
  }
  return results;
}

/**
 * Build item-relative presence sets without collapsing the two sequence roles.
 * Stored dependency and prerequisite edges are first normalized to the same
 * predecessor → dependent meaning.
 */
export function getSequenceRoleItemIds(edges: ItemRelationshipEdge[]): SequenceRoleItemIds {
  const hasPredecessors = new Set<string>();
  const hasDependents = new Set<string>();

  for (const edge of edges) {
    const pair = normalizeSequencePair(edge);
    if (!pair) continue;
    hasDependents.add(pair.predecessorItemId);
    hasPredecessors.add(pair.dependentItemId);
  }

  return { hasPredecessors, hasDependents };
}

export interface MirrorPairResult {
  dependencyEdge: ItemRelationshipEdge;
  prerequisiteEdge: ItemRelationshipEdge;
}

/**
 * PRE-DEP-1: both a Dependency and Prerequisite on the same directed pair.
 * Pair order is normalized as predecessor → dependent.
 */
export function findMirrorPair(
  edges: ItemRelationshipEdge[],
  predecessorItemId: string,
  dependentItemId: string,
): MirrorPairResult | null {
  const dep = edges.find(
    (e) =>
      e.type === 'dependency' &&
      e.status !== 'dissolved' &&
      e.sourceItemId === dependentItemId &&
      e.targetItemId === predecessorItemId,
  );
  const pre = edges.find(
    (e) =>
      e.type === 'prerequisite' &&
      e.status !== 'dissolved' &&
      e.sourceItemId === predecessorItemId &&
      e.targetItemId === dependentItemId,
  );
  if (!dep || !pre) return null;
  return { dependencyEdge: dep, prerequisiteEdge: pre };
}

/** All mirror pairs in an edge set. */
export function findAllMirrorPairs(edges: ItemRelationshipEdge[]): MirrorPairResult[] {
  const results: MirrorPairResult[] = [];
  const seen = new Set<string>();

  for (const edge of edges) {
    const normalized = normalizeSequencePair(edge);
    if (!normalized) continue;
    const key = [normalized.predecessorItemId, normalized.dependentItemId].sort().join(':');
    if (seen.has(key)) continue;
    const mirror = findMirrorPair(
      edges,
      normalized.predecessorItemId,
      normalized.dependentItemId,
    );
    if (mirror) {
      seen.add(key);
      results.push(mirror);
    }
  }
  return results;
}

// ---------------------------------------------------------------------------
// Sequence strength — one rule for both assertion directions.
//
// Canon 12 §5.4 and the glossary's Dependency entry: Dependency and
// Prerequisite are one constraint asserted from opposite ends ("the same
// subtypes apply to Prerequisite"; a mirror pair is "identical regardless of
// asserter"), with four strengths:
//
//   informational  context only; never flags, never gates
//   soft           warns at commitment; never blocks
//   hard           blocks commitment until the required item is addressed;
//                  ranked above that item it is a Prioritization Inversion
//   gate           candidacy / scope entry; stronger than hard, flagged never
//                  disallowed (owner ruling 2026-10-12)
//
// Until migration 1004 a prerequisite edge with no subtype read as hard here
// while a dependency edge with no subtype read as not hard, so the same fact
// gated differently depending on which item asserted it. Now an edge with no
// stated strength never gates, the database refuses to store one (1004), and
// a writer with no strength to state writes the floor, `soft`.
// ---------------------------------------------------------------------------

export const SEQUENCE_STRENGTHS: readonly DependencySubtype[] = ['informational', 'soft', 'hard', 'gate'];

/** What a writer records when nobody said how strongly two items are related in sequence. */
export const DEFAULT_SEQUENCE_STRENGTH: DependencySubtype = 'soft';

/** The strength picker's words, shared by every modal that asks for one. */
export const SEQUENCE_STRENGTH_OPTIONS: ReadonlyArray<{
  value: DependencySubtype;
  label: string;
  description: string;
}> = [
  { value: 'informational', label: 'Informational', description: 'Context only. Never flags, never blocks.' },
  { value: 'soft', label: 'Soft', description: 'Warns at commitment. Never blocks.' },
  {
    value: 'hard',
    label: 'Hard',
    description:
      'Must be addressed before the dependent item can be committed. Ranked above the item it needs, it is flagged as an inversion.',
  },
  {
    value: 'gate',
    label: 'Gate (scope entry)',
    description: 'The dependent item cannot be done at all without this one. Flagged wherever it is ranked without it.',
  },
];

export function isSequenceEdgeType(type: string): type is 'dependency' | 'prerequisite' {
  return type === 'dependency' || type === 'prerequisite';
}

export function isSequenceStrength(value: unknown): value is DependencySubtype {
  return typeof value === 'string' && (SEQUENCE_STRENGTHS as readonly string[]).includes(value);
}

/** `hard` and `gate` gate; `soft` and `informational` never do. A missing strength never does. */
export function isHardStrength(subtype: string | null | undefined): boolean {
  return subtype === 'hard' || subtype === 'gate';
}

/**
 * The same test over a bare row — type, subtype, status — for callers that hold
 * rows rather than edges (the pool check reads raw Supabase rows).
 */
export function isHardSequenceLink(link: { type: string; subtype?: string | null; status?: string | null }): boolean {
  if (!isSequenceEdgeType(link.type)) return false;
  if (link.status === 'dissolved' || link.status === 'dismissed') return false;
  return isHardStrength(link.subtype);
}

/**
 * Hard sequence edges only — the publish gate's inversion detector, the planning
 * bridge and the health page all read strength through this one rule.
 */
export function isHardSequenceEdge(edge: ItemRelationshipEdge): boolean {
  return isHardSequenceLink(edge);
}
